import type { PortalCatalogItem, PortalReadyLaterBucket } from "~/@types/portal";

import formatLocaleNumber from "~/helpers/localeNumberFormat";
import MoneyFormat from "~/helpers/moneyFormat";
import { dayFormat, formatDateLabel, parseYmdToLocalDate, stringFormatDate } from "~/helpers/timestampFormat";
import i18n from "~/i18n/i18n";
import orderLineUnits from "~fulfill/orders/helpers/orderLineUnits";
import pricingBasisLabels from "~fulfill/product/helpers/pricingBasisLabels";
import shippingUomLabels from "~fulfill/product/helpers/shippingUomLabels";

export interface PortalSpecRow {
	label: string;
	value: string;
}

export interface PortalLastOrdered {
	orderDate: string;
	unitPrice: number;
}

type LastOrderedItem = Pick<
	PortalCatalogItem,
	"browseOnly" | "lastOrderedDate" | "lastOrderedPrice" | "priceUnavailable"
>;

export const canShowItemPricing = (item: Pick<PortalCatalogItem, "browseOnly" | "priceUnavailable">): boolean =>
	!item.browseOnly && !item.priceUnavailable;

export const resolveLastOrdered = (item: LastOrderedItem): PortalLastOrdered | null => {
	if (!canShowItemPricing(item)) return null;
	const { lastOrderedDate, lastOrderedPrice } = item;
	if (lastOrderedPrice == null || lastOrderedPrice <= 0 || !lastOrderedDate) return null;
	if (!stringFormatDate(lastOrderedDate)) return null;
	return { orderDate: lastOrderedDate, unitPrice: lastOrderedPrice };
};

export const formatLastOrderedSummary = (item: LastOrderedItem): string => {
	const lastOrdered = resolveLastOrdered(item);
	if (!lastOrdered) return "";
	return `${MoneyFormat(lastOrdered.unitPrice, "USD")} · ${stringFormatDate(lastOrdered.orderDate)}`;
};

export const describeLastOrdered = (item: LastOrderedItem): string => {
	const summary = formatLastOrderedSummary(item);
	return summary ? `Last ordered ${summary}` : "";
};

export const formatQuickOrderSummary = (itemCount: number, total: number): string =>
	`${itemCount} ${itemCount === 1 ? "item" : "items"} · ${MoneyFormat(total, "USD")}`;

export const isPortalCallForPrice = (price: null | number | undefined): boolean => (price ?? 0) <= 0;

export const formatPortalItemPrice = (price: number): string =>
	isPortalCallForPrice(price) ? "Call for price" : MoneyFormat(price, "USD");

const formatMeasurement = (value: number | null | undefined, unit: string): string =>
	value == null ? "" : `${Number(value.toFixed(2))}${unit}`;

export const buildItemSpecRows = (item: PortalCatalogItem): PortalSpecRow[] =>
	[
		{ label: "SKU", value: item.sku },
		{ label: "Container", value: item.containerSize },
		{ label: "Unit of measure", value: item.uom },
		{ label: "Category", value: item.category },
		{ label: "Type", value: item.itemType ?? "" },
		{ label: "Group", value: item.genus },
		{ label: "Height", value: formatMeasurement(item.heightInches, '"') },
		{ label: "Length", value: formatMeasurement(item.lengthInches, '"') },
		{ label: "Width", value: formatMeasurement(item.widthInches, '"') },
		{ label: "Weight", value: formatMeasurement(item.weightLbs, " lb") },
		{ label: "UPC", value: item.upc ?? "" },
		{ label: "Last ordered", value: formatLastOrderedSummary(item) }
	].filter((row) => row.value.trim().length > 0);

export const describeAvailabilityTiming = (item: PortalCatalogItem): string => {
	if (!item.readyDate) return "";
	const formattedDate = stringFormatDate(item.readyDate);
	if (!formattedDate) return "";
	return item.readyWeek == null ? `Ready ${formattedDate}` : `Ready week ${item.readyWeek} · ${formattedDate}`;
};

export const buildItemImageGallery = (item: PortalCatalogItem): string[] => {
	const portalPhotos = (item.portalPhotoUrls ?? []).map((url) => url.trim()).filter((url) => url !== "");
	if (portalPhotos.length > 0) return portalPhotos;
	const primary = item.imageUrl?.trim() ?? "";
	return primary ? [primary] : [];
};

export type AvailableUnitsSource = Pick<PortalCatalogItem, "displayEachesPerUnit" | "shippingUomLabel">;

const SINGLE_EACH_PER_UNIT = 1;

export const isDisplayUnitsEligible = (
	item: AvailableUnitsSource
): item is AvailableUnitsSource & { displayEachesPerUnit: number; shippingUomLabel: string } =>
	item.displayEachesPerUnit != null && Boolean(item.shippingUomLabel);

export const hasEligibleUnitRows = (items: readonly PortalCatalogItem[]): boolean => items.some(isDisplayUnitsEligible);

export const availableUnitsOf = (eaches: number, item: AvailableUnitsSource): null | number =>
	isDisplayUnitsEligible(item) ? orderLineUnits.truncateUnits(eaches, item.displayEachesPerUnit) : null;

export const portalQtyCellTooltip = (item: AvailableUnitsSource): string =>
	item.shippingUomLabel
		? shippingUomLabels.unitCellTooltip(item.shippingUomLabel, item.displayEachesPerUnit)
		: shippingUomLabels.unitCellTooltip(shippingUomLabels.singleEachLabel, SINGLE_EACH_PER_UNIT);

export const gridQuantityOf = (
	eaches: null | number,
	item: AvailableUnitsSource,
	showAvailableEaches: boolean
): null | number =>
	eaches == null || !showAvailableEaches
		? eaches
		: (availableUnitsOf(eaches, item) ?? (item.shippingUomLabel ? null : eaches));

export const formatGridQuantity = (value: unknown, showAvailableEaches: boolean): string =>
	showAvailableEaches ? orderLineUnits.formatUnits(value) : orderLineUnits.formatEaches(value);

export const formatAvailableUnits = (eaches: number, item: AvailableUnitsSource): null | string => {
	if (!isDisplayUnitsEligible(item)) return null;
	const units = orderLineUnits.truncateUnits(eaches, item.displayEachesPerUnit);
	return orderLineUnits.formatUngroupedUnits(units, item.shippingUomLabel);
};

export const formatAvailableWithUnits = (eaches: number, item: AvailableUnitsSource, fallback: string): string => {
	if (!isDisplayUnitsEligible(item) || eaches === 0) return fallback;
	const units = formatLocaleNumber(orderLineUnits.truncateUnits(eaches, item.displayEachesPerUnit));
	return tPortal("catalog.readyDates.unitsWithEaches", { eaches: formatLocaleNumber(eaches), units });
};

export const formatGridEaches = (
	eaches: null | number,
	item: AvailableUnitsSource,
	showAvailableEaches: boolean
): string => {
	if (eaches == null || !showAvailableEaches) return formatGridQuantity(eaches, false);
	return formatAvailableWithUnits(eaches, item, formatGridQuantity(gridQuantityOf(eaches, item, true), true));
};

export const formatGridReady = (
	eaches: null | number,
	item: AvailableUnitsSource,
	showAvailableEaches: boolean
): string => {
	const quantity = eaches ? formatGridEaches(eaches, item, showAvailableEaches) : "";
	return quantity ? tPortal("preorder.grid.ready", { quantity }) : "";
};

export const formatPreorderReady = (
	eaches: null | number,
	later: null | { quantity: number; readyDate: string },
	item: AvailableUnitsSource,
	showUnits: boolean
): string => {
	const ready = formatGridReady(eaches, item, showUnits);
	if (!later) return ready;
	const quantity = formatGridEaches(later.quantity, item, showUnits);
	if (!quantity) return ready;
	const next = tPortal("preorder.grid.laterThisWeek", {
		date: formatShortDate(later.readyDate),
		quantity
	});
	return ready ? tPortal("preorder.grid.readyWithLater", { later: next, ready }) : next;
};

export const formatGridEntry = (entry: number, item: AvailableUnitsSource, showAvailableEaches: boolean): string => {
	if (!showAvailableEaches || !isDisplayUnitsEligible(item)) return formatLocaleNumber(entry);
	return formatAvailableWithUnits(entry * item.displayEachesPerUnit, item, formatLocaleNumber(entry));
};

type ReadyDateBuckets = Pick<PortalCatalogItem, "availabilityByReadyDate" | "availableNowQuantity">;

type ReadyNowSource = AvailableUnitsSource & Pick<PortalCatalogItem, "availableNowQuantity" | "availableQuantity">;

type ReadyLaterSource = AvailableUnitsSource & Pick<PortalCatalogItem, "readyLaterBuckets">;

export interface PortalReadyDateRow {
	key: string;
	label: string;
	quantity: string;
}

export const tPortal = (key: string, options?: Record<string, unknown>): string =>
	i18n.t(key, { ns: "portal", ...options });

export const formatCatalogPriceCell = (
	item: Pick<PortalCatalogItem, "browseOnly" | "priceUnavailable"> | undefined,
	price: null | number | undefined
): string => {
	if (item?.priceUnavailable) return tPortal("catalog.priceUnavailable");
	if (item && !canShowItemPricing(item)) return "";
	if (price == null) return "";
	return formatPortalItemPrice(price);
};

export const formatShortDate = (ymd: string): string => {
	const date = parseYmdToLocalDate(ymd);
	return date ? formatDateLabel(date, dayFormat) : ymd;
};

const formatBucketReadyDate = ({ readyDate }: Pick<PortalReadyLaterBucket, "readyDate">): string =>
	formatShortDate(readyDate.slice(0, 10));

const formatReadyQuantity = (eaches: number, item: AvailableUnitsSource): string =>
	formatAvailableWithUnits(eaches, item, formatLocaleNumber(eaches));

export const selectReadyLaterBuckets = ({
	availabilityByReadyDate,
	availableNowQuantity
}: ReadyDateBuckets): PortalReadyLaterBucket[] | null => {
	if (availabilityByReadyDate == null || availableNowQuantity == null) return null;
	const lastNowIndex = availabilityByReadyDate.reduce(
		(found, bucket, index) => (bucket.cumulativeAvailableQuantity === availableNowQuantity ? index : found),
		-1
	);
	let baseline = Math.max(availableNowQuantity, 0);
	const laterBuckets: PortalReadyLaterBucket[] = [];
	for (const bucket of availabilityByReadyDate.slice(lastNowIndex + 1)) {
		const readyQuantity = bucket.cumulativeAvailableQuantity - baseline;
		baseline = Math.max(baseline, bucket.cumulativeAvailableQuantity);
		if (bucket.readyDate != null && readyQuantity > 0) {
			laterBuckets.push({ ...bucket, readyDate: bucket.readyDate, readyQuantity });
		}
	}
	return laterBuckets;
};

export const hasReadyDateBuckets = (item: Pick<PortalCatalogItem, "readyLaterBuckets">): boolean =>
	item.readyLaterBuckets != null;

export const getReadyNowQuantity = (
	item: Pick<PortalCatalogItem, "availableNowQuantity" | "availableQuantity">
): number => item.availableNowQuantity ?? item.availableQuantity;

export const sumReadyLaterQuantity = (item: Pick<PortalCatalogItem, "readyLaterBuckets">): null | number => {
	const buckets = item.readyLaterBuckets ?? [];
	return buckets.length === 0 ? null : buckets.reduce((total, bucket) => total + bucket.readyQuantity, 0);
};

export const describeReadyNow = (item: ReadyNowSource): string => {
	const quantity = getReadyNowQuantity(item);
	return quantity > 0
		? tPortal("catalog.readyDates.now", { quantity: formatReadyQuantity(quantity, item) })
		: tPortal("catalog.readyDates.noneNow");
};

export const describeReadyLater = (bucket: PortalReadyLaterBucket, item: AvailableUnitsSource): string =>
	tPortal("catalog.readyDates.later", {
		date: formatBucketReadyDate(bucket),
		quantity: formatReadyQuantity(bucket.readyQuantity, item)
	});

export const describeReadyFrom = (bucket: PortalReadyLaterBucket): string =>
	tPortal("catalog.readyDates.from", { date: formatBucketReadyDate(bucket) });

const describeReadyLaterRowLabel = (bucket: PortalReadyLaterBucket): string =>
	bucket.readyWeek == null
		? tPortal("catalog.readyDates.laterRowLabel", { date: formatBucketReadyDate(bucket) })
		: tPortal("catalog.readyDates.laterRowLabelWithWeek", {
				date: formatBucketReadyDate(bucket),
				week: bucket.readyWeek
			});

export const buildReadyDateRows = (item: ReadyNowSource & ReadyLaterSource): PortalReadyDateRow[] => {
	if (!hasReadyDateBuckets(item)) return [];
	const nowQuantity = getReadyNowQuantity(item);
	return [
		{
			key: "now",
			label: tPortal("catalog.readyDates.nowRowLabel"),
			quantity: nowQuantity > 0 ? formatReadyQuantity(nowQuantity, item) : tPortal("catalog.readyDates.noneQuantity")
		},
		...(item.readyLaterBuckets ?? []).map((bucket) => ({
			key: bucket.readyDate,
			label: describeReadyLaterRowLabel(bucket),
			quantity: formatReadyQuantity(bucket.readyQuantity, item)
		}))
	];
};

export const describeReadyLaterCaption = (item: Pick<PortalCatalogItem, "readyLaterBuckets">): string => {
	const nextReady = item.readyLaterBuckets?.[0];
	return nextReady ? tPortal("catalog.readyDates.laterCaption", { date: formatBucketReadyDate(nextReady) }) : "";
};

export type PortalPricePair =
	| { eachPrice: string; unitLabel: null; unitPrice: null }
	| { eachPrice: string; unitLabel: string; unitPrice: string };

export const portalPricePerUnit = (eachPrice: number, item: AvailableUnitsSource): number =>
	isDisplayUnitsEligible(item) ? eachPrice * item.displayEachesPerUnit : eachPrice;

export const catalogPricePerUnitValue = (
	item: AvailableUnitsSource & Pick<PortalCatalogItem, "browseOnly" | "price" | "priceUnavailable">,
	tierOverridePrice: number | undefined
): null | number => {
	if (!canShowItemPricing(item)) return null;
	const eachPrice = tierOverridePrice ?? item.price;
	return isPortalCallForPrice(eachPrice) ? null : portalPricePerUnit(eachPrice, item);
};

export const portalPricePair = (eachPrice: number, item: AvailableUnitsSource): PortalPricePair => {
	const formattedEachPrice = formatPortalItemPrice(eachPrice);
	if (
		isPortalCallForPrice(eachPrice) ||
		!isDisplayUnitsEligible(item) ||
		!pricingBasisLabels.hasMultiEachPack(item.displayEachesPerUnit)
	) {
		return { eachPrice: formattedEachPrice, unitLabel: null, unitPrice: null };
	}
	return {
		eachPrice: formattedEachPrice,
		unitLabel: item.shippingUomLabel,
		unitPrice: formatPortalItemPrice(portalPricePerUnit(eachPrice, item))
	};
};

type SkuLineSource = AvailableUnitsSource &
	Pick<PortalCatalogItem, "browseOnly" | "containerSize" | "price" | "priceUnavailable" | "sku" | "uom">;

export const buildItemSkuLine = (item: SkuLineSource): string => {
	const isUnitNamedOnPriceLine = canShowItemPricing(item) && Boolean(portalPricePair(item.price, item).unitLabel);
	const shouldNameUnitInSkuLine = isDisplayUnitsEligible(item) && !isUnitNamedOnPriceLine;
	return [item.sku, shouldNameUnitInSkuLine ? item.uom : item.containerSize || item.uom].filter(Boolean).join(" · ");
};

const formatCartItemSubtitle = (sku: string, containerSize: string): string =>
	[sku, containerSize].filter(Boolean).join(" - ");

export default formatCartItemSubtitle;
